import { useState } from "react";
import { InputField } from "../../../components/forms/InputField";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validateCurrency } from "../../../utils/sharedValidators";
import {
  sanitizeCurrencyInput,
  parseAndFormatCurrency,
} from "../../../utils/format";
import { validateCurrencyWithRestriction } from "../utils/questionnaire";
import { useGetCurrentUserQuery } from "../../../api/users";
import { useUpsertAnswerMutation } from "../../../api/answers";
import { InputWrapper, ErrorWrapper } from "./SharedFormStyles";

interface CurrencyFieldInputProps {
  question: QuestionWithRelations;
  documentId: string;
}

const CurrencyFieldInput: React.FC<CurrencyFieldInputProps> = ({
  question,
  documentId,
}) => {
  const [currencyInput, setCurrencyInput] = useState<string | null>(
    question.answers[0]?.valueTyped
      ? parseAndFormatCurrency(question.answers[0].valueTyped)
      : null,
  );
  const [currencyError, setCurrencyError] = useState<string | null>(null);

  const { data: user } = useGetCurrentUserQuery();
  const [
    upsertAnswer,
    {
      /*isLoading: isCreating,*/
      isError: isUpsertError,
      /*isSuccess: isCreateSuccess,*/
      /*isSuccess: isUpdateSuccess,*/
      reset: resetUpdateAnswer,
    },
  ] = useUpsertAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetUpdateAnswer();

    setCurrencyInput(e.target.value);
    if (!user) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      documentId,
      rootQuestionId: question.rootQuestionId ?? question.id,
      sectionId: question.parentQuestionnaireSectionId,
      serializeDebounceMs: 1000,
    };

    // For empty values, only check required field validation
    if (e.target.value === "") {
      const restrictionErrorMessage = validateCurrencyWithRestriction(
        "",
        question,
      );
      setCurrencyError(restrictionErrorMessage);
      if (restrictionErrorMessage) {
        if (hasAnswer) {
          void upsertAnswer({ ...payload, valueTyped: null });
        }
        return;
      }
      if (!hasAnswer) return;
      void upsertAnswer({ ...payload, valueTyped: "" });
      return;
    }

    // For non-empty values, validate basic currency format first
    const basicError = validateCurrency(e.target.value);
    if (basicError) {
      setCurrencyError(basicError);
      if (hasAnswer) {
        void upsertAnswer({ ...payload, valueTyped: null });
      }
      return;
    }

    // Then validate with input restrictions
    const normalizedValue = sanitizeCurrencyInput(e.target.value);
    const restrictionErrorMessage = validateCurrencyWithRestriction(
      normalizedValue,
      question,
    );
    setCurrencyError(restrictionErrorMessage);

    if (restrictionErrorMessage) {
      if (hasAnswer) {
        void upsertAnswer({ ...payload, valueTyped: null });
      }
      return;
    }

    void upsertAnswer({ ...payload, valueTyped: normalizedValue }); // normalized value saved to DB
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const normalizedValue = sanitizeCurrencyInput(e.target.value);

    if (!user) return;
    const payload = {
      questionId: question.id,
      userId: user.id,
      documentId,
      rootQuestionId: question.rootQuestionId ?? question.id,
      sectionId: question.parentQuestionnaireSectionId,
      serializeDebounceMs: 1000,
    };
    // For empty values, only check required field validation
    if (normalizedValue === "") {
      const restrictionErrorMessage = validateCurrencyWithRestriction(
        "",
        question,
      );
      setCurrencyError(restrictionErrorMessage);
      if (restrictionErrorMessage) return;
      if (!hasAnswer) return;
      void upsertAnswer({ ...payload, valueTyped: "" });
      return;
    }

    // For non-empty values, validate basic currency format first
    const basicError = validateCurrency(normalizedValue);
    if (basicError) {
      setCurrencyError(basicError);
      return;
    }

    // Then validate with input restrictions
    const restrictionErrorMessage = validateCurrencyWithRestriction(
      normalizedValue,
      question,
    );
    setCurrencyError(restrictionErrorMessage);
    if (restrictionErrorMessage) return;

    const formatted = parseAndFormatCurrency(normalizedValue);
    if (formatted) {
      setCurrencyInput(formatted);
      void upsertAnswer({ ...payload, valueTyped: normalizedValue }); // normalized value saved to DB
    }
  };

  // Clear the currency error when the input field is focused and user is typing
  const handleFocus = () => {
    setCurrencyError(null);
  };

  //const isLoading = isCreating || isUpdating;
  const hasError = isUpsertError;
  /*
  const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !currencyError &&
    currencyInput !== "";
  */

  return (
    <>
      <InputWrapper>
        <InputField
          type="text"
          inputMode="decimal" // Shows decimal keyboard on mobile
          value={currencyInput ?? ""}
          onChange={(e) => {
            handleCurrencyChange(e);
          }}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder="$0.00"
          errorMessage={currencyError}
          required={question.isRequired}
        />
        {/* Commenting out the loader and success icon for now as per the request of the design team */}
        {/*
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
        */}
      </InputWrapper>
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default CurrencyFieldInput;
